import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../../api/apiServices";

// ── Helpers ───────────────────────────────────────────────────
function fmtAmt(n)  { return Number(n || 0).toLocaleString("en-NG"); }
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-NG", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}
function fmtDateShort(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

// ── Type meta ─────────────────────────────────────────────────
const TYPE_META = {
  buy:              { label: "Buy",         icon: "ri-arrow-down-line",         color: "#059669", bg: "#ECFDF5" },
  sell:             { label: "Sell",        icon: "ri-arrow-up-line",           color: "#DC2626", bg: "#FEF2F2" },
  deposit:          { label: "Deposit",     icon: "ri-arrow-down-circle-line",  color: "#059669", bg: "#ECFDF5" },
  withdraw_crypto:  { label: "Withdraw",    icon: "ri-arrow-up-circle-line",    color: "#D97706", bg: "#FFFBEB" },
  withdrawal:       { label: "Withdraw",    icon: "ri-arrow-up-circle-line",    color: "#D97706", bg: "#FFFBEB" },
  withdraw_naira:   { label: "Transfer",    icon: "ri-bank-line",               color: "#D97706", bg: "#FFFBEB" },
  bill_payment:     { label: "Bill",        icon: "ri-receipt-line",            color: "#7C3AED", bg: "#F5F3FF" },
  admin_adjustment: { label: "Adjustment",  icon: "ri-settings-3-line",         color: "#64748B", bg: "#F8FAFC" },
};
function getTxMeta(type) { return TYPE_META[type] || { label: type, icon: "ri-exchange-line", color: "#64748B", bg: "#F8FAFC" }; }

// ── Status pill ───────────────────────────────────────────────
const STATUS_STYLE = {
  completed:  { color: "#059669", bg: "#ECFDF5" },
  pending:    { color: "#D97706", bg: "#FFFBEB" },
  processing: { color: "#0284C7", bg: "#EFF6FF" },
  failed:     { color: "#DC2626", bg: "#FEF2F2" },
  reversed:   { color: "#7C3AED", bg: "#F5F3FF" },
  delivered:  { color: "#059669", bg: "#ECFDF5" },
};
function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { color: "#64748B", bg: "#F8FAFC" };
  return (
    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full capitalize"
      style={{ background: s.bg, color: s.color }}>
      {status}
    </span>
  );
}

// ── TYPE FILTERS ──────────────────────────────────────────────
const TYPE_FILTERS = [
  { value: "",               label: "All Types" },
  { value: "buy",            label: "Buy" },
  { value: "sell",           label: "Sell" },
  { value: "deposit",        label: "Deposit" },
  { value: "withdraw_naira", label: "Transfer" },
  { value: "withdraw_crypto",label: "Withdraw Crypto" },
  { value: "bill_payment",   label: "Bills" },
];
const STATUS_FILTERS = [
  { value: "",           label: "All Status" },
  { value: "pending",    label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "completed",  label: "Completed" },
  { value: "failed",     label: "Failed" },
];
const MANUAL_STATUSES = ["pending", "processing", "completed", "failed", "reversed"];

// ─────────────────────────────────────────────────────────────
// Detail Panel
// ─────────────────────────────────────────────────────────────
function TxDetailPanel({ tx, onClose, onStatusUpdated }) {
  const [newStatus, setNewStatus]     = useState(tx.status);
  const [note, setNote]               = useState("");
  const [updating, setUpdating]       = useState(false);
  const [msg, setMsg]                 = useState(null);
  const meta = getTxMeta(tx.type);

  const handleUpdate = async () => {
    if (newStatus === tx.status) return;
    setUpdating(true);
    try {
      const res = await apiClient.patch(`/mtrade/admin/transactions/${tx._id}/status`, { status: newStatus, note });
      if (res.data.status) {
        setMsg({ type: "success", text: res.data.message });
        onStatusUpdated(tx._id, newStatus);
      }
    } catch (err) {
      setMsg({ type: "error", text: err?.response?.data?.message || "Update failed" });
    } finally { setUpdating(false); }
  };

  const rows = [
    ["Transaction ID", tx._id],
    ["Type",           meta.label],
    ["User ID",        tx.user],
    ["Email",          tx.userInfo?.email],
    ["Username",       tx.userInfo?.userName],
    ["Asset",          tx.asset],
    ["Naira Amount",   `₦${fmtAmt(tx.nairaAmount)}`],
    tx.cryptoAmount > 0 && ["Crypto Amount", `${tx.cryptoAmount} ${tx.asset}`],
    tx.feeAmount    > 0 && ["Fee",            `₦${fmtAmt(tx.feeAmount)}`],
    tx.chargeAmount > 0 && ["Charged",        `₦${fmtAmt(tx.chargeAmount)}`],
    ["Paystack Ref",   tx.paystackReference || tx.reference || "—"],
    ["Date",           fmtDate(tx.createdAt)],
    tx.description  && ["Description",        tx.description],
    tx.adminNote    && ["Admin Note",          tx.adminNote],
  ].filter(Boolean);

  // Bill-specific
  if (tx.type === "bill_payment") {
    if (tx.metadata?.phone)    rows.push(["Phone",    tx.metadata.phone]);
    if (tx.metadata?.network)  rows.push(["Network",  tx.metadata.network]);
    if (tx.metadata?.disco)    rows.push(["DISCO",    tx.metadata.disco]);
    if (tx.metadata?.provider) rows.push(["Provider", tx.metadata.provider]);
  }
  // Sell/withdraw
  if (tx.bankName)      rows.push(["Bank",    tx.bankName]);
  if (tx.accountNumber) rows.push(["Account", tx.accountNumber]);

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-full max-w-lg bg-[#0f0f0f] flex flex-col shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: meta.bg + "20" }}>
              <i className={`${meta.icon} text-sm`} style={{ color: meta.color }} />
            </div>
            <div>
              <p className="text-white font-bold text-sm">{meta.label} · ₦{fmtAmt(tx.nairaAmount)}</p>
              <p className="text-white/40 text-xs">{fmtDateShort(tx.createdAt)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={tx.status} />
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10">
              <i className="ri-close-line text-white/60" />
            </button>
          </div>
        </div>

        {/* Details */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Field list */}
          <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
            {rows.map(([label, value]) => value && (
              <div key={label} className="flex justify-between items-start px-4 py-3 border-b border-white/4 last:border-0 gap-4">
                <span className="text-white/40 text-xs flex-shrink-0">{label}</span>
                <span className="text-white/80 text-xs font-medium text-right break-all">{value}</span>
              </div>
            ))}
          </div>

          {/* Status override */}
          <div className="bg-white/4 rounded-xl border border-white/5 p-4 space-y-3">
            <p className="text-white text-sm font-semibold">Manual Status Override</p>
            <p className="text-white/30 text-xs">
              Use with caution. Changing to <span className="text-[#00C896]">completed</span> on a deposit will credit the user's wallet.
              Changing to <span className="text-purple-400">reversed</span> on a completed deposit will debit it.
            </p>

            {msg && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium ${
                msg.type === "success"
                  ? "bg-[#00C896]/10 border border-[#00C896]/20 text-[#00C896]"
                  : "bg-red-500/10 border border-red-500/20 text-red-400"
              }`}>
                <i className={msg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
                {msg.text}
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              {MANUAL_STATUSES.map(s => (
                <button key={s} onClick={() => setNewStatus(s)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold capitalize transition-all border ${
                    newStatus === s
                      ? "border-[#00C896]/40 bg-[#00C896]/15 text-[#00C896]"
                      : "border-white/8 bg-white/4 text-white/40 hover:text-white/70"
                  }`}>
                  {s}
                </button>
              ))}
            </div>

            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder="Reason / note (saved to transaction)"
              className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />

            <button onClick={handleUpdate}
              disabled={newStatus === tx.status || updating}
              className="w-full py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-40">
              {updating ? "Updating…" : newStatus === tx.status ? "No changes" : `Set to "${newStatus}"`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Overview stat cards
// ─────────────────────────────────────────────────────────────
function OverviewCards({ overview }) {
  if (!overview) return null;
  const cards = [
    { label: "Today's Volume",  value: `₦${fmtAmt(overview.today.total)}`,  sub: `${overview.today.count} txns`,  color: "#00C896" },
    { label: "This Week",       value: `₦${fmtAmt(overview.week.total)}`,   sub: `${overview.week.count} txns`,   color: "#7C3AED" },
    { label: "This Month",      value: `₦${fmtAmt(overview.month.total)}`,  sub: `${overview.month.count} txns`,  color: "#0284C7" },
    { label: "Pending",         value: overview.pending,                    sub: "awaiting",                       color: "#D97706", warn: overview.pending > 0 },
    { label: "Failed",          value: overview.failed,                     sub: "this period",                    color: "#DC2626", warn: overview.failed > 0  },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {cards.map(c => (
        <div key={c.label}
          className={`bg-[#161616] rounded-xl border p-3.5 ${c.warn ? "border-red-500/20" : "border-white/5"}`}>
          <p className="text-white/40 text-[10px] uppercase tracking-wide mb-1">{c.label}</p>
          <p className="text-white font-bold text-lg" style={{ color: c.warn ? c.color : undefined }}>
            {c.value}
          </p>
          <p className="text-white/25 text-[10px] mt-0.5">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main view
// ─────────────────────────────────────────────────────────────
export default function TransactionsView() {
  const [txns, setTxns]               = useState([]);
  const [overview, setOverview]       = useState(null);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(null);
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [total, setTotal]             = useState(0);
  const [stats, setStats]             = useState(null);

  // Filters
  const [search, setSearch]           = useState("");
  const [typeFilter, setTypeFilter]   = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate]       = useState("");
  const [toDate, setToDate]           = useState("");
  const [sortBy, setSortBy]           = useState("newest");
  const searchTimer                   = useRef(null);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await apiClient.get("/mtrade/admin/transactions/overview");
      if (res.data.status) setOverview(res.data.data);
    } catch { /* silent */ }
  }, []);

  const fetchTxns = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: p, limit: 25,
        search: search.trim(),
        type: typeFilter, status: statusFilter,
        from: fromDate, to: toDate, sortBy,
      }).toString();
      const res = await apiClient.get(`/mtrade/admin/transactions?${params}`);
      if (res.data.status) {
        setTxns(p === 1 ? res.data.data : prev => [...prev, ...res.data.data]);
        setTotalPages(res.data.pagination.pages);
        setTotal(res.data.pagination.total);
        setStats(res.data.stats);
        setPage(p);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [search, typeFilter, statusFilter, fromDate, toDate, sortBy]);

  useEffect(() => { fetchOverview(); }, []);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setTxns([]); fetchTxns(1); }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search, typeFilter, statusFilter, fromDate, toDate, sortBy]);

  const handleStatusUpdated = (txId, newStatus) => {
    setTxns(prev => prev.map(t => t._id === txId ? { ...t, status: newStatus } : t));
    setSelected(prev => prev?._id === txId ? { ...prev, status: newStatus } : prev);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">Transactions</h2>
          <p className="text-white/40 text-xs mt-0.5">
            {total.toLocaleString()} total
            {stats && stats.pendingCount > 0 && (
              <span className="text-amber-400"> · {stats.pendingCount} pending</span>
            )}
            {stats && stats.failedCount > 0 && (
              <span className="text-red-400"> · {stats.failedCount} failed</span>
            )}
          </p>
        </div>
        <button onClick={() => fetchTxns(1)}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors">
          <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Overview cards */}
      <OverviewCards overview={overview} />

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex gap-2">
          {/* Search */}
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search reference, user ID, phone…"
              className="w-full bg-[#161616] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
          </div>
          {/* Sort */}
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="amount_desc">Highest</option>
            <option value="amount_asc">Lowest</option>
          </select>
        </div>

        <div className="flex gap-2 flex-wrap">
          {/* Type */}
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none">
            {TYPE_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>

          {/* Status */}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none">
            {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>

          {/* Date range */}
          <div className="flex items-center gap-1.5">
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none focus:border-[#00C896]/40 [color-scheme:dark]" />
            <span className="text-white/20 text-xs">to</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none focus:border-[#00C896]/40 [color-scheme:dark]" />
          </div>

          {/* Clear */}
          {(typeFilter || statusFilter || fromDate || toDate || search) && (
            <button onClick={() => { setTypeFilter(""); setStatusFilter(""); setFromDate(""); setToDate(""); setSearch(""); }}
              className="px-3 py-2.5 text-xs text-white/40 hover:text-white/70 bg-white/4 rounded-xl border border-white/8 transition-colors">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Transaction table */}
      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">
        {/* Column headers */}
        <div className="hidden sm:grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr] px-5 py-3 border-b border-white/5 text-[10px] font-bold text-white/25 uppercase tracking-wider">
          <span>Transaction</span>
          <span>User</span>
          <span>Amount</span>
          <span>Status</span>
          <span>Date</span>
        </div>

        {loading && txns.length === 0 ? (
          <div className="divide-y divide-white/4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <div className="w-8 h-8 rounded-lg bg-white/5 animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2"><div className="h-3 w-32 bg-white/5 rounded animate-pulse" /><div className="h-2.5 w-24 bg-white/5 rounded animate-pulse" /></div>
                <div className="h-3 w-20 bg-white/5 rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : txns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mb-3">
              <i className="ri-exchange-line text-white/20 text-xl" />
            </div>
            <p className="text-white/30 text-sm">No transactions found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-white/4">
              {txns.map(tx => {
                const meta = getTxMeta(tx.type);
                return (
                  <div key={tx._id}
                    onClick={() => setSelected(tx)}
                    className="sm:grid sm:grid-cols-[2fr_1.5fr_1fr_1fr_1fr] flex flex-col gap-1 px-5 py-3.5 hover:bg-white/3 cursor-pointer transition-colors">

                    {/* Type + reference */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: meta.bg + "20" }}>
                        <i className={`${meta.icon} text-xs`} style={{ color: meta.color }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium">
                          {meta.label}
                          {tx.asset && tx.asset !== "NGN" && (
                            <span className="text-white/40 text-xs ml-1">{tx.asset}</span>
                          )}
                          {tx.assetName && (
                            <span className="text-white/40 text-xs ml-1 truncate"> · {tx.assetName}</span>
                          )}
                        </p>
                        <p className="text-white/25 text-[10px] font-mono truncate">
                          {(tx.paystackReference || tx.reference || tx._id).slice(0, 20)}
                        </p>
                      </div>
                    </div>

                    {/* User */}
                    <div className="min-w-0 flex items-center sm:block">
                      <p className="text-white/60 text-xs truncate">{tx.userInfo?.email || tx.user?.slice(0, 16)}</p>
                      {tx.userInfo?.userName && (
                        <p className="text-white/30 text-[10px] truncate">{tx.userInfo.userName}</p>
                      )}
                    </div>

                    {/* Amount */}
                    <div className="flex items-center sm:block">
                      <p className="text-white text-sm font-bold">₦{fmtAmt(tx.nairaAmount)}</p>
                      {tx.cryptoAmount > 0 && (
                        <p className="text-white/30 text-[10px]">{tx.cryptoAmount} {tx.asset}</p>
                      )}
                    </div>

                    {/* Status */}
                    <div className="flex items-center">
                      <StatusPill status={tx.status} />
                    </div>

                    {/* Date */}
                    <div className="flex items-center">
                      <p className="text-white/30 text-xs">{fmtDateShort(tx.createdAt)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load more */}
            {page < totalPages && (
              <div className="px-5 py-3 border-t border-white/4">
                <button onClick={() => fetchTxns(page + 1)} disabled={loading}
                  className="w-full py-2.5 text-xs font-semibold text-[#00C896] hover:text-[#00b386] transition-colors disabled:opacity-50 text-center">
                  {loading ? "Loading…" : `Load more (${total - txns.length} remaining)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <TxDetailPanel
          tx={selected}
          onClose={() => setSelected(null)}
          onStatusUpdated={handleStatusUpdated}
        />
      )}
    </div>
  );
}
